import { Button, FormControl, InputLabel, MenuItem, Select, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs, { Dayjs } from 'dayjs';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { usePlansApi } from 'src/modules/plans/api';
import { autoRenewToggleSx, datePickerContainerSx } from '../../helpers';
import { Plan } from 'src/modules/plans/types';

export type CreateCompanySubscriptionData = {
  planId: string;
  startType: 'date' | 'onboarding_complete';
  startDate?: string;
  autoRenew: boolean;
};

interface AddSubscriptionStepProps {
  applicationId?: string;
  subscriptionData: CreateCompanySubscriptionData | null;
  onBack: () => void;
  onSkip: () => void;
  onSubmit: (data: CreateCompanySubscriptionData | null) => void;
  onSubscriptionDataChange: (data: CreateCompanySubscriptionData) => void;
}

export const AddSubscriptionStep: FC<AddSubscriptionStepProps> = ({
  applicationId,
  subscriptionData,
  onBack,
  onSkip,
  onSubmit,
  onSubscriptionDataChange,
}) => {
  const { getAdminPlans } = usePlansApi();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>(subscriptionData?.planId ?? '');
  const [startType, setStartType] = useState<'date' | 'onboarding_complete'>(
    subscriptionData?.startType ?? 'onboarding_complete',
  );
  const [startDate, setStartDate] = useState<Dayjs | null>(
    subscriptionData?.startDate ? dayjs(subscriptionData.startDate) : null,
  );
  const [autoRenew, setAutoRenew] = useState<boolean>(subscriptionData?.autoRenew ?? true);

  useEffect(() => {
    setSelectedPlanId(subscriptionData?.planId ?? '');
    setStartType(subscriptionData?.startType ?? 'onboarding_complete');
    setStartDate(subscriptionData?.startDate ? dayjs(subscriptionData.startDate) : null);
    setAutoRenew(subscriptionData?.autoRenew ?? true);
  }, [subscriptionData]);

  useEffect(() => {
    if (selectedPlanId) {
      const data: CreateCompanySubscriptionData = {
        planId: selectedPlanId,
        startType,
        startDate: startType === 'date' && startDate ? startDate.format('YYYY-MM-DD') : undefined,
        autoRenew,
      };
      onSubscriptionDataChange(data);
    }
  }, [selectedPlanId, startType, startDate, autoRenew, onSubscriptionDataChange]);

  useEffect(() => {
    if (!applicationId) {
      setPlans([]);
      return;
    }

    setLoadingPlans(true);
    getAdminPlans({
      limit: 10000,
      offset: 0,
      applicationId,
      types: 'public,private',
    })
      .then(response => {
        setPlans(response.data.items ?? []);
      })
      .catch(() => {
        setPlans([]);
      })
      .finally(() => {
        setLoadingPlans(false);
      });
  }, [applicationId, getAdminPlans]);

  useEffect(() => {
    if (selectedPlanId && !plans.some(plan => plan.id === selectedPlanId)) {
      setSelectedPlanId('');
    }
  }, [plans, selectedPlanId]);

  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
    [plans],
  );

  const isValid = !!selectedPlanId && (startType === 'onboarding_complete' || !!startDate);

  const handleNext = () => {
    if (!selectedPlanId || (startType === 'date' && !startDate)) {
      return;
    }

    onSubmit({
      planId: selectedPlanId,
      startType,
      startDate: startType === 'date' ? startDate!.format('YYYY-MM-DD') : undefined,
      autoRenew,
    });
  };

  return (
    <Stack spacing={2} mt={2.5}>

      <FormControl disabled={loadingPlans}>
        <InputLabel>Subscription</InputLabel>
        <Select
          MenuProps={{ disableScrollLock: true }}
          value={selectedPlanId}
          label="Subscription"
          onChange={event => setSelectedPlanId(event.target.value)}
        >
          {sortedPlans.map(plan => (
            <MenuItem value={plan.id} key={plan.id}>
              {plan.name} (${plan.price})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Stack direction="row" gap={1} alignItems="flex-start">
        <FormControl>
          <InputLabel>Start Type</InputLabel>
          <Select
            MenuProps={{ disableScrollLock: true }}
            value={startType}
            label="Start Type"
            onChange={event => setStartType(event.target.value as 'date' | 'onboarding_complete')}
          >
            <MenuItem value="date">Specific date</MenuItem>
            <MenuItem value="onboarding_complete">Account Setup Complete</MenuItem>
          </Select>
        </FormControl>

        {startType === 'date' && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={['DatePicker']} sx={datePickerContainerSx}>
              <DatePicker
                closeOnSelect={false}
                format="MM/DD/YYYY"
                minDate={dayjs()}
                onChange={value => setStartDate(value)}
                value={startDate}
              />
            </DemoContainer>
          </LocalizationProvider>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="body2">Auto Renew:</Typography>
        <ToggleButtonGroup
          sx={autoRenewToggleSx}
          size="small"
          color="primary"
          value={autoRenew}
          exclusive
          onChange={(_, value) => {
            if (typeof value === 'boolean') {
              setAutoRenew(value);
            }
          }}
        >
          <ToggleButton value={false}>Off</ToggleButton>
          <ToggleButton value={true}>On</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack direction="row" justifyContent="space-between">
        <Button type="button" onClick={onBack} variant="outlined" color="secondary" size="large">
          Back
        </Button>
        <Stack direction="row" gap={1}>
          <Button type="button" onClick={onSkip} variant="outlined" color="secondary" size="large">
            Skip
          </Button>
          <Button type="button" onClick={handleNext} disabled={!isValid} variant="contained" color="secondary" size="large">
            Next
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
